/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { createColumnHelper } from '@tanstack/react-table';
import { BudgetOptimiserTableRow, SpendAmount } from './budget-optimiser.types';
import { CurrencyDisplayWrapper } from 'features/currency/currencyDisplayWrapper';
import { sum } from '../charts/mmm-chart-helpers';
import { MmmModelOutput } from '../mmmModelOutput';

export const createBudgetOptimiserTableRows = (
  mmmData: MmmModelOutput,
  spendAmounts: SpendAmount[]
) => {
  const sourceCategoryRows = mmmData?.forecasts.map((forecast) => {
    const spendAmount = spendAmounts.find(
      (sa) => sa.sourceCategoryName === forecast.sourceCategoryDisplayName
    )!;

    const optimisedRevenue =
      (spendAmount.optimisedSpendAmount > 0
        ? forecast.returnValues[spendAmount.optimisedSpendAmount]
        : forecast.returnValues[0]) * mmmData.dayMultiplier;

    const optimisedRevenueWithDayMultiplier =
      optimisedRevenue < 1 ? 1 : optimisedRevenue / mmmData.dayMultiplier;

    const budgetOptimiserRow: BudgetOptimiserTableRow = {
      rowName: forecast.sourceCategoryDisplayName,
      currentSpend: forecast.spend * mmmData.dayMultiplier,
      currentRevenue: forecast.revenue * mmmData.dayMultiplier,
      optimisedSpend: spendAmount.optimisedSpendAmount * mmmData.dayMultiplier,
      optimisedRevenue,
      changeAmount:
        (spendAmount.optimisedSpendAmount - forecast.spend) *
        mmmData.dayMultiplier,
      roas:
        optimisedRevenueWithDayMultiplier === 1
          ? 0
          : optimisedRevenueWithDayMultiplier / spendAmount.optimisedSpendAmount
    };

    return budgetOptimiserRow;
  });

  const totalRow: BudgetOptimiserTableRow = {
    rowName: 'Total',
    currentSpend: sum(sourceCategoryRows.map((row) => row.currentSpend)),
    currentRevenue: sum(sourceCategoryRows.map((row) => row.currentRevenue)),
    optimisedSpend: sum(sourceCategoryRows.map((row) => row.optimisedSpend)),
    optimisedRevenue: sum(
      sourceCategoryRows.map((row) => row.optimisedRevenue)
    ),
    changeAmount: sum(sourceCategoryRows.map((row) => row.changeAmount)),
    roas:
      sum(sourceCategoryRows.map((row) => row.optimisedRevenue)) /
      sum(sourceCategoryRows.map((row) => row.optimisedSpend))
  };

  return [...sourceCategoryRows, totalRow];
};

const budgetOptimiserHelper = createColumnHelper<BudgetOptimiserTableRow>();

export const createBudgetOptimiserTableColumns = (currencyCode: string) => [
  budgetOptimiserHelper.accessor((row) => row.rowName, {
    id: 'source-category',
    header: () => <span>Source</span>,
    enableColumnFilter: false,
    cell: (info) => <span>{info.getValue()}</span>,
    size: 80
  }),
  budgetOptimiserHelper.group({
    header: 'Current',
    enableSorting: false,
    enableColumnFilter: false,
    columns: [
      budgetOptimiserHelper.accessor((row) => row.currentSpend, {
        id: 'current-spend',
        header: () => <span>Spend</span>,
        enableSorting: false,
        enableColumnFilter: false,
        size: 110,
        cell: (info) => (
          <CurrencyDisplayWrapper currencyCode={currencyCode}>
            {Intl.NumberFormat('en-GB', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(info.getValue())}
          </CurrencyDisplayWrapper>
        )
      }),
      budgetOptimiserHelper.accessor((row) => row.currentRevenue, {
        id: 'current-revenue',
        header: () => <span>Revenue</span>,
        enableSorting: false,
        enableColumnFilter: false,
        size: 110,
        cell: (info) => (
          <CurrencyDisplayWrapper currencyCode={currencyCode}>
            {Intl.NumberFormat('en-GB', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(info.getValue())}
          </CurrencyDisplayWrapper>
        )
      })
    ]
  }),
  budgetOptimiserHelper.group({
    header: 'Optimised',
    enableSorting: false,
    enableColumnFilter: false,
    columns: [
      budgetOptimiserHelper.accessor((row) => row.optimisedSpend, {
        id: 'optimised-spend',
        header: () => <span>Spend</span>,
        enableSorting: false,
        enableColumnFilter: false,
        size: 110,
        cell: (info) => (
          <CurrencyDisplayWrapper currencyCode={currencyCode}>
            {Intl.NumberFormat('en-GB', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(info.getValue())}
          </CurrencyDisplayWrapper>
        )
      }),
      budgetOptimiserHelper.accessor((row) => row.optimisedRevenue, {
        id: 'optimised-revenue',
        header: () => <span>Revenue</span>,
        enableSorting: false,
        enableColumnFilter: false,
        size: 110,
        cell: (info) => (
          <CurrencyDisplayWrapper currencyCode={currencyCode}>
            {Intl.NumberFormat('en-GB', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(info.getValue())}
          </CurrencyDisplayWrapper>
        )
      })
    ]
  }),
  budgetOptimiserHelper.group({
    header: 'Change',
    enableSorting: false,
    enableColumnFilter: false,
    columns: [
      budgetOptimiserHelper.accessor((row) => row.changeAmount, {
        id: 'change-amount',
        header: () => <span>Amount</span>,
        enableSorting: false,
        enableColumnFilter: false,
        size: 110,
        cell: (info) => (
          <CurrencyDisplayWrapper currencyCode={currencyCode}>
            {Intl.NumberFormat('en-GB', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(info.getValue())}
          </CurrencyDisplayWrapper>
        )
      }),
      budgetOptimiserHelper.accessor((row) => row.roas, {
        id: 'change-roas',
        header: () => <span>ROAS</span>,
        enableSorting: false,
        enableColumnFilter: false,
        size: 40,
        cell: (info) => (
          <span className="text-sm">
            {Intl.NumberFormat('en-GB', {
              maximumFractionDigits: 1
            }).format(info.getValue())}
          </span>
        )
      })
    ]
  })
];
